import {
  CheckCircle2,
  Clock,
  Loader2,
  Play,
  ShieldCheck,
  ShieldOff,
  Square,
  TriangleAlert,
} from "lucide-react";
import { Fragment, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { DateTime } from "luxon";
import tw from "twin.macro";

import {
  buildTemplateKey,
  EventStage,
  NotificationType,
  ProtectionWatcherMode,
} from "@bienesis/common";
import { useProtectionSummary } from "@/api/protection/queries";
import {
  PROTECTION_KEY_TO_RISK_TYPE,
  NotificationEventItemHttp,
  ProtectionActionItemHttp,
} from "@/api/protection/types";

import { ProtectionKey } from "./enums/protection-key.enum";
import { ProtectionSettingsCard } from "./protection-settings";
import SectionTitle from "@/components/section-title.component";

const INITIAL_COUNT = 4;
const INCREMENT = 5;

const formatDate = (iso: string) =>
  DateTime.fromISO(iso).setLocale("fr").toFormat("dd LLL à HH:mm");

const formatDuration = (startIso: string, endIso: string) => {
  const minutes = Math.max(
    0,
    Math.round(
      DateTime.fromISO(endIso).diff(DateTime.fromISO(startIso), "minutes")
        .minutes
    )
  );
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return rest ? `${hours} h ${rest} min` : `${hours} h`;
};

type ActionGroup =
  | {
      kind: "linked";
      id: string;
      begin: ProtectionActionItemHttp;
      end: ProtectionActionItemHttp;
    }
  | { kind: "single"; id: string; action: ProtectionActionItemHttp };

// Actions arrive most-recent-first. END actions reference their opening BEGIN
// via `startEventId`; we pair them into a single card while keeping the global
// chronological order (the pair is placed at the END position, its most recent
// event). A BEGIN with no matching END stays a standalone card.
function groupActions(actions: ProtectionActionItemHttp[]): ActionGroup[] {
  const byId = new Map(actions.map((a) => [a._id, a]));
  const consumed = new Set<string>();
  const groups: ActionGroup[] = [];

  for (const action of actions) {
    if (consumed.has(action._id)) continue;

    if (
      action.stage === EventStage.END &&
      action.startEventId &&
      byId.has(action.startEventId)
    ) {
      const begin = byId.get(action.startEventId)!;
      consumed.add(begin._id);
      groups.push({ kind: "linked", id: begin._id, begin, end: action });
    } else {
      groups.push({ kind: "single", id: action._id, action });
    }
  }

  return groups;
}

const Card = tw.div`bg-white rounded-xl border border-gray-200 shadow-sm`;

const TimelineNode = ({ variant }: { variant: "begin" | "end" }) => (
  <div
    css={[
      tw`w-10 h-10 shrink-0 rounded-full flex items-center justify-center text-primary-700`,
      variant === "begin" && tw`bg-green-200`,
      variant === "end" && tw`bg-purple-100`,
      variant === "end" && tw`text-purple-700`,
    ]}
  >
    {variant === "begin" ? (
      <Play tw="w-3.5 h-3.5 fill-white stroke-white" />
    ) : (
      <Square tw="w-3 h-3 fill-purple-500 stroke-none" />
    )}
  </div>
);

function getTemplateKey(notification: NotificationEventItemHttp) {
  if (
    notification.type === NotificationType.DETECTION &&
    (!notification.stage || !notification.mode)
  ) {
    return null;
  }

  return buildTemplateKey(notification);
}

function NotificationKindChip({ mode }: { mode: ProtectionWatcherMode }) {
  const { t } = useTranslation();
  if (mode === ProtectionWatcherMode.ACTIVE) {
    return (
      <span tw="self-start flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700">
        <ShieldCheck tw="w-3 h-3" />
        {t("protection.notificationKind.active")}
      </span>
    );
  }
  return (
    <span tw="self-start flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-500">
      <ShieldOff tw="w-3 h-3" />
      {t("protection.notificationKind.passive")}
    </span>
  );
}

function ForecastKindChip() {
  const { t } = useTranslation();
  return (
    <span tw="self-start flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-700">
      <TriangleAlert tw="w-3 h-3" />
      {t("protection.notificationKind.forecast")}
    </span>
  );
}

function NotificationRow({
  notification,
  isLast,
}: {
  notification: NotificationEventItemHttp;
  isLast: boolean;
}) {
  const { t } = useTranslation();
  const templateKey = getTemplateKey(notification);

  return (
    <div
      css={[
        tw`flex flex-col gap-1.5 py-2.5`,
        !isLast && tw`border-b border-gray-100`,
      ]}
    >
      <p tw="text-xs text-gray-400">{formatDate(notification.occurredAt)}</p>
      <p tw="text-sm font-normal text-gray-600">
        {templateKey
          ? t(`protection.notificationKey.${templateKey}` as never)
          : t("protection.notificationKey.unknown")}
      </p>
      {notification.type === NotificationType.FORECAST ? (
        <ForecastKindChip />
      ) : (
        notification.mode && <NotificationKindChip mode={notification.mode} />
      )}
    </div>
  );
}

function LinkedActionCard({
  begin,
  end,
}: {
  begin: ProtectionActionItemHttp;
  end: ProtectionActionItemHttp;
}) {
  const { t } = useTranslation();
  return (
    <div tw="rounded-xl border border-gray-200 bg-gray-50 p-3">
      <div tw="flex gap-3">
        <div tw="flex flex-col items-center">
          <TimelineNode variant="begin" />
          <div tw="w-0.5 flex-1 my-1 bg-primary-200 rounded-full" />
          <TimelineNode variant="end" />
        </div>
        <div tw="flex-1 flex flex-col">
          <div tw="flex items-start justify-between gap-2">
            <div>
              <p tw="text-sm font-medium text-gray-700 ">
                {t(`protectionActions.${begin.action}` as never)}
              </p>
              <p tw="text-xs text-gray-400">
                {formatDate(begin.commandSentAt)}
              </p>
            </div>
            <span tw="self-start shrink-0 flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700">
              <CheckCircle2 tw="w-3 h-3" />
              {t("protection.actionStatusDone")}
            </span>
          </div>
          <div tw="my-2 self-start flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-primary-50 text-primary-700 text-xs font-medium">
            <Clock tw="w-3.5 h-3.5" />
            {formatDuration(begin.commandSentAt, end.commandSentAt)}
            {/* {t("protection.actionDuration", {
              duration: formatDuration(begin.commandSentAt, end.commandSentAt),
            })} */}
          </div>
          <div>
            <p tw="text-sm font-medium text-gray-700">
              {t(`protectionActions.${end.action}` as never)}
            </p>
            <p tw="text-xs text-gray-400">{formatDate(end.commandSentAt)}</p>
          </div>
        </div>
      </div>
    </div>
  );
}

function SingleActionCard({ action }: { action: ProtectionActionItemHttp }) {
  const { t } = useTranslation();
  return (
    <div tw="rounded-xl border border-gray-200 bg-gray-50 p-3 flex items-center gap-3">
      <TimelineNode
        variant={action.stage === EventStage.BEGIN ? "begin" : "end"}
      />
      <div>
        <p tw="text-sm font-medium text-gray-500">
          {t(`protectionActions.${action.action}` as never)}
        </p>
        <p tw="text-xs text-gray-400">{formatDate(action.commandSentAt)}</p>
      </div>
    </div>
  );
}

function NotificationsSection({
  notifications,
  hasMore,
  isFetchingMore,
  onShowMore,
}: {
  notifications: NotificationEventItemHttp[];
  hasMore: boolean;
  isFetchingMore: boolean;
  onShowMore: () => void;
}) {
  const { t } = useTranslation();
  const [visibleCount, setVisibleCount] = useState(INITIAL_COUNT);

  const visible = notifications.slice(0, visibleCount);
  const canRevealMore = visibleCount < notifications.length;
  const showMoreButton = canRevealMore || hasMore;

  const handleShowMore = () => {
    const next = visibleCount + INCREMENT;
    setVisibleCount(next);
    if (next >= notifications.length && hasMore) onShowMore();
  };

  return (
    <div tw="flex flex-col">
      <SectionTitle tone="primary">
        {t("protection.notificationsHistory")}
      </SectionTitle>
      <Card tw="p-4 flex flex-col gap-3">
        {notifications.length === 0 ? (
          <p tw="text-sm text-gray-400">{t("protection.noNotifications")}</p>
        ) : (
          <Fragment>
            <div tw="flex flex-col">
              {visible.map((n, i) => (
                <NotificationRow
                  key={n._id}
                  notification={n}
                  isLast={i === visible.length - 1}
                />
              ))}
            </div>
            {showMoreButton && (
              <button
                type="button"
                disabled={isFetchingMore}
                tw="text-sm font-medium text-primary-800 text-center pt-1 border-t border-gray-100 active:opacity-70 transition disabled:opacity-40"
                onClick={handleShowMore}
              >
                {isFetchingMore ? (
                  <Loader2 tw="w-4 h-4 animate-spin inline" />
                ) : (
                  t("global.showMore")
                )}
              </button>
            )}
          </Fragment>
        )}
      </Card>
    </div>
  );
}

function AutomaticActionsSection({
  actions,
  hasMore,
  isFetchingMore,
  onShowMore,
}: {
  actions: ProtectionActionItemHttp[];
  hasMore: boolean;
  isFetchingMore: boolean;
  onShowMore: () => void;
}) {
  const { t } = useTranslation();
  const [visibleCount, setVisibleCount] = useState(INITIAL_COUNT);

  const groups = useMemo(() => groupActions(actions), [actions]);

  const visible = groups.slice(0, visibleCount);
  const canRevealMore = visibleCount < groups.length;
  const showMoreButton = canRevealMore || hasMore;

  const handleShowMore = () => {
    const next = visibleCount + INCREMENT;
    setVisibleCount(next);
    if (next >= groups.length && hasMore) onShowMore();
  };

  return (
    <div tw="flex flex-col">
      <SectionTitle tone="primary">{t("protection.actionsHistory")}</SectionTitle>
      <Card tw="p-4 flex flex-col gap-3">
        {actions.length === 0 ? (
          <p tw="text-sm text-gray-400">{t("protection.noAutomaticActions")}</p>
        ) : (
          <Fragment>
            <div tw="flex flex-col gap-2.5">
              {visible.map((group) =>
                group.kind === "linked" ? (
                  <LinkedActionCard
                    key={group.id}
                    begin={group.begin}
                    end={group.end}
                  />
                ) : (
                  <SingleActionCard key={group.id} action={group.action} />
                )
              )}
            </div>
            {showMoreButton && (
              <button
                type="button"
                disabled={isFetchingMore}
                tw="text-sm font-medium text-primary-800 text-center pt-1 border-t border-gray-100 active:opacity-70 transition disabled:opacity-40"
                onClick={handleShowMore}
              >
                {isFetchingMore ? (
                  <Loader2 tw="w-4 h-4 animate-spin inline" />
                ) : (
                  t("global.showMore")
                )}
              </button>
            )}
          </Fragment>
        )}
      </Card>
    </div>
  );
}

export default function ProtectionDetailSheetBody({
  areaId,
  protectionKey,
}: {
  areaId: string;
  protectionKey: ProtectionKey;
}) {
  const { t } = useTranslation();
  const riskType = PROTECTION_KEY_TO_RISK_TYPE[protectionKey];

  const { data, isLoading, isError, isFetchingNextPage, fetchNextPage } =
    useProtectionSummary({ areaId, riskType });

  const allNotifications = useMemo(
    () => data?.pages.flatMap((p) => p.notificationEvents.items) ?? [],
    [data]
  );

  const allActions = useMemo(
    () => data?.pages.flatMap((p) => p.protectionActions.items) ?? [],
    [data]
  );

  const lastPage = data?.pages[data.pages.length - 1];
  const activeSince = data?.pages[0]?.activeSince ?? null;
  const notifHasMore = lastPage?.notificationEvents.hasMore ?? false;
  const actionsHasMore = lastPage?.protectionActions.hasMore ?? false;

  return (
    <div tw="flex flex-col gap-4 px-4 pb-6">
      {activeSince && (
        <p tw="text-xs text-gray-500 font-medium">
          {t("protection.activeSince", {
            date: DateTime.fromISO(activeSince)
              .setLocale("fr")
              .toFormat("dd/MM HH:mm"),
          })}
        </p>
      )}

      <ProtectionSettingsCard areaId={areaId} protectionKey={protectionKey} />

      {isError ? (
        <Card tw="flex items-center gap-2 px-4 py-3 text-sm text-red-600">
          <TriangleAlert tw="w-4 h-4 shrink-0" />
          Erreur de chargement des données de protection.
        </Card>
      ) : isLoading ? (
        <Card tw="flex items-center justify-center gap-2 px-4 py-8 text-sm text-gray-500">
          <Loader2 tw="w-4 h-4 animate-spin" />
          Chargement…
        </Card>
      ) : (
        <Fragment>
          <AutomaticActionsSection
            actions={allActions}
            hasMore={actionsHasMore}
            isFetchingMore={isFetchingNextPage}
            onShowMore={fetchNextPage}
          />
          <NotificationsSection
            notifications={allNotifications}
            hasMore={notifHasMore}
            isFetchingMore={isFetchingNextPage}
            onShowMore={fetchNextPage}
          />
        </Fragment>
      )}
    </div>
  );
}
